import { message } from 'antd';
import debounce from 'lodash.debounce';
import type { SorterResult } from 'antd/es/table/interface';
import axios, { type AxiosResponse, isAxiosError } from 'axios';
import qs from 'qs';

import type { CallResponse, TakeCallPayload } from '~/redux/pointage/slice';

import type { Call, Campaign, Contact, Group, Model, NewModel, PointageStats, User } from '~/types/models';
import type { IRestIndexParams } from '~/types/states';
import {
  type AttachResponse,
  type DetachResponse,
  type ErrorResponse,
  type ImportResponse,
  type IndexResponse,
  isErrorResponse,
  type PointageInitResponse,
  type SendUnsentEmailsResponse,
  type SendUnsentSmsResponse,
} from '~/types/responses';
import type { ImportRequest } from '~/types/requests';

import dayjs from '~/dayjs';

const notify = debounce((text) => message.error(text, 10), 100);

export function parseParams<T extends Model>(params: Partial<IRestIndexParams<T>>): Record<string, string | string[]> {
  const parsedParams: Record<string, string | string[]> = {};

  for (const key of Object.keys(params)) {
    const value = params[key as keyof IRestIndexParams<T>];
    if (Array.isArray(value)) {
      parsedParams[key] = value.join(',');
    } else if (value !== undefined && value !== null) {
      parsedParams[key] = String(value);
    }
  }

  return parsedParams;
}

export const API_PATH = process.env.REACT_APP_API_PATH ?? '/api/v1';
export const LOGOUT_URL = process.env.REACT_APP_LOGOUT_URL ?? '';

export const addContactsToCampaign = async (
  campaign: Campaign,
  params: Partial<IRestIndexParams<Contact>>,
): Promise<AttachResponse> => {
  const parsedParams = parseParams<Contact>(params);
  const { data } = await axios.post<AttachResponse>(
    `${API_PATH}/campaigns/${campaign.id}/contacts?${qs.stringify(parsedParams)}`,
  );
  return data;
};

export const addContactsToGroup = async (
  group: Group,
  params: Partial<IRestIndexParams<Contact>>,
): Promise<AttachResponse> => {
  const parsedParams = parseParams<Contact>(params);
  const { data } = await axios.post<AttachResponse>(
    `${API_PATH}/groups/${group.id}/contacts?${qs.stringify(parsedParams)}`,
  );
  return data;
};

export const addDocumentationPageSeen = async (name: string): Promise<void> => {
  await axios.put(`${API_PATH}/auth/documentation`, name, {
    headers: {
      'Content-Type': 'text/plain',
    },
  });
};

const ephemerealCache: Record<string, Promise<any> | any[]> = {};
const ephemerealCacheTtl: Record<string, number> = {};
export const cache = async <T>(key: string, callback: () => Promise<T>, ttl: number = -1): Promise<T> => {
  if (ephemerealCacheTtl[key] && ephemerealCacheTtl[key] !== -1 && ephemerealCacheTtl[key] < new Date().getTime()) {
    delete ephemerealCache[key];
  }

  ephemerealCacheTtl[key] = ttl === -1 ? ttl : new Date().getTime() + ttl;

  if (ephemerealCache[key]) {
    return await ephemerealCache[key];
  }

  ephemerealCache[key] = callback();

  return await ephemerealCache[key];
};

export const clearCache = (regex: RegExp): void => {
  for (const key in ephemerealCache) {
    if (key.match(regex)) {
      delete ephemerealCache[key];
    }
  }
};

export const create = async <R extends Model>(path: string, item: NewModel<R>): Promise<R> => {
  const response = await axios.post<R>(`${API_PATH}${path}`, item);
  return response.data;
};

export const destroy = async (path: string, id: string): Promise<void> => {
  await axios.delete(`${API_PATH}${path}/${id}`, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const fetchBackendVersion = async (): Promise<string> => {
  const { data } = await axios.get<string>(`${API_PATH}/auth/version`);
  return data;
};

export const fetchPointageInit = async (): Promise<PointageInitResponse> => {
  const { data } = await axios.get<PointageInitResponse>(`${API_PATH}/pointage/init`);
  return data;
};

export const fetchPointageStats = async ({
  campaignIds,
  groupIds,
}: {
  campaignIds?: string;
  groupIds?: string}): Promise<PointageStats> => {
  const { data } = await axios.get<PointageStats>(`${API_PATH}/pointage/stats`, {
    params: {
      campaignIds,
      groupIds,
    },
  });

  return data;
};

export const fetchUser = async (): Promise<User> => {
  const { data } = await axios.get<User>(`${API_PATH}/auth/user`);
  return data;
};

export const freeCall = async (id: string): Promise<void> => {
  await axios.put(`${API_PATH}/calls/${id}/free`);
};

export const getCampaignNextCall = async (id: string, skippedContactIds: string[] = []): Promise<Call | null> => {
  const { status, data: pointageCall } = await axios.post<Call>(`${API_PATH}/campaigns/${id}/calls/next`, {
    skippedContactIds: skippedContactIds.join(','),
  });

  if (status === 204) {
    return null;
  }

  return pointageCall;
};

export const getGroupNextCall = async (id: string, skippedContactIds: string[] = []): Promise<Call | null> => {
  const { status, data: pointageCall } = await axios.post<Call>(`${API_PATH}/groups/${id}/calls/next`, {
    skippedContactIds: skippedContactIds.join(','),
  });

  if (status === 204) {
    return null;
  }

  return pointageCall;
};

export const handleError = (e: unknown): void => {
  if (isAxiosError<ErrorResponse | string>(e) && e.response) {
    if (isErrorResponse(e.response.data)) {
      return;
    }

    const contentType = e.response.headers['Content-Type'];
    const url = new URL(e.request.responseURL);

    switch (e.response.status) {
      case 403:
        notify(e.response.data || "Vous n'avez pas les permissions requises.");
        return;
      case 404:
        notify(e.response.data || 'Ressource introuvable.');
        return;
      case 405:
        notify(`Erreur de configuration du système: la route ${url.pathname} ne répond pas.`);
        break;
      case 503:
        notify('Service indisponible. Veuillez patienter.');
        break;
      default:
        if (typeof contentType === 'string' && contentType.includes('text/plain')) {
          notify(e.response.data ?? 'Erreur serveur');
          break;
        }

        notify('Erreur serveur');
        break;
    }
    return;
  }

  throw e;
};

export async function downloadErrorLines(
  path: string,
  { file, lines }: { file: File, lines: string[]; },
): Promise<void> {
  const formData = new FormData();

  formData.append('file', file);

  lines.forEach((c) => {
    formData.append('lines[]', c);
  });

  const response = await axios({
    method: 'POST',
    url: `${API_PATH}/${path}/import/errors`,
    data: formData,
    responseType: 'blob',
  });

  const href = URL.createObjectURL(response.data);

  const link = document.createElement('a');
  link.href = href;
  link.setAttribute('download', `${dayjs().format('YYYY-MM-DD')} Erreurs Pointage.csv`);
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

export async function importEntities(
  path: 'contacts' | 'groups' | 'users',
  request: ImportRequest,
): Promise<ImportResponse | null>;
export async function importEntities(
  path: string,
  { groups, keys, columns, file, behaviour }: ImportRequest,
): Promise<ImportResponse | null> {
  const formData = new FormData();

  formData.append('file', file);

  if (groups) {
    groups.forEach((g) => {
      formData.append('groups[]', g);
    });
  }

  columns.forEach((c) => {
    formData.append('columns[]', c);
  });

  keys.forEach((c) => {
    formData.append('keys[]', c);
  });

  formData.append('behaviour', behaviour);

  try {
    const response = await axios<ImportResponse>({
      method: 'POST',
      url: `${API_PATH}/${path}/import`,
      data: formData,
    });

    return response.data;
  } catch (e) {
    if (isAxiosError<ErrorResponse>(e) && e.response && isErrorResponse(e.response.data)) {
      switch (e.response.data.code) {
        case 'conflicting_keys':
        case 'unsupported_mime_type':
          message.error(e.response?.data.message, 10);
          return null;
      }
    }

    throw e;
  }
}

export const index = async <R extends Model>(
  path: string,
  params: Partial<IRestIndexParams<R>>,
): Promise<IndexResponse<R>> => {
  const { data } = await axios.get<IndexResponse<R>>(`${API_PATH}${path}`, {
    params: parseParams<R>(params),
  });
  return data;
};

export const massDestroy = async <R extends Model>(
  path: string,
  params: Partial<IRestIndexParams<R>>,
): Promise<void> => {
  await axios.delete(`${API_PATH}${path}`, {
    headers: {
      'Content-Type': 'application/json',
    },
    params,
  });
};

export const options = async <T = Record<string, any>>(path: string): Promise<T> => {
  const { data } = await axios.options<T>(`${API_PATH}${path}`);
  return data;
};

export const removeContactsFromCampaign = async (
  campaign: Campaign,
  params: Partial<IRestIndexParams<Contact>>,
): Promise<DetachResponse> => {
  const parsedParams = parseParams<Contact>(params);
  const { data } = await axios.delete<DetachResponse>(
    `${API_PATH}/campaigns/${campaign.id}/contacts?${qs.stringify(parsedParams)}`,
  );
  return data;
};

export const removeContactsFromGroup = async (
  group: Group,
  params: Partial<IRestIndexParams<Contact>>,
): Promise<DetachResponse> => {
  const parsedParams = parseParams<Contact>(params);
  const { data } = await axios.delete<DetachResponse>(
    `${API_PATH}/groups/${group.id}/contacts?${qs.stringify(parsedParams)}`,
  );
  return data;
};

export const retrieve = async <R extends Model>(
  path: string,
  params: Partial<IRestIndexParams<R>> = {},
): Promise<R> => {
  const { data } = await axios.get<R>(`${API_PATH}${path}`, { params });
  return data;
};

export const saveCallResponse = async (callResponse: CallResponse, id: string): Promise<void> => {
  await axios.put(`${API_PATH}/calls/${id}/response`, callResponse);
};

export const search = async <R extends Model>(
  path: string,
  q: string,
): Promise<R> => {
  const { data } = await axios.get<R>(`${API_PATH}${path}/search`, { params: { q } });
  return data;
};

export const sendUnsentEmails = async (id: string): Promise<SendUnsentEmailsResponse> => {
  const { data } = await axios.put<SendUnsentEmailsResponse>(`${API_PATH}/campaigns/${id}/send_unsent_emails`);
  return data;
};

export const sendUnsentSms = async (id: string): Promise<SendUnsentSmsResponse> => {
  const { data } = await axios.put<SendUnsentSmsResponse>(`${API_PATH}/campaigns/${id}/send_unsent_sms`);
  return data;
};

export const takeCall = async ({
  type,
  id,
  contactId,
}: TakeCallPayload): Promise<Call> => {
  let response: AxiosResponse;

  switch (type) {
    case 'CallCampaign':
      response = await axios.put<Call>(`${API_PATH}/campaigns/${id}/calls/take/${contactId}`);
      break;
    case 'Group':
      response = await axios.put<Call>(`${API_PATH}/groups/${id}/calls/take/${contactId}`);
      break;
  }

  return response.data;
}

export const update = async <R extends Model>(
  path: string,
  item: R,
  params: Partial<IRestIndexParams<R>> = {},
): Promise<R> => {
  const response = await axios.put<R>(`${API_PATH}${path}`, item, {
    params,
  });
  return response.data;
};

const convertSingleSorterToSortBy = <T extends Model>(sorter: SorterResult<T>): string => {
  return `${sorter.order === 'descend' ? '-' : ''}${sorter.field as string}`;
};
export const convertSorterToSortBy = <T extends Model>(
  sorter: SorterResult<T> | Array<SorterResult<T>>,
): string | null => {
  const sorterArray: Array<SorterResult<T>> = [];

  if (Array.isArray(sorter)) {
    sorterArray.push(...sorter);
  } else {
    sorterArray.push(sorter);
  }

  const result = sorterArray
    .filter((i) => !!i.order)
    .reduce<string[]>((acc, i) => {
      acc.push(convertSingleSorterToSortBy(i));
      return acc;
    }, [])
    .join('');

  if (result.length === 0) {
    return null;
  }

  return result;
};
